import styled from 'styled-components'
import React, {useEffect, useState} from 'react'
import TextField from '@material-ui/core/TextField'
import {handleAutocomplete, onChangeStringHandler} from 'lib/dom'
import {Autocomplete} from '@material-ui/lab'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import {ajax, useObserve} from 'lib/rx'
import {debounceTime, filter, map, switchMap, tap} from 'rxjs/operators'
import {useAuthToken} from 'obvio/auth'
import Button from '@material-ui/core/Button'
import {useToggle} from 'lib/toggle'
import Dialog from 'lib/ui/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import CreateNewTagForm from 'organization/Event/Services/Apps/HighLevel/Config/CreateNewTagForm'

/**
 * Minimum amount of chars required to perform a search.
 */
const MIN_SEARCH_CHARS = 3

export default function TagsAutocomplete(props: {
  value: string | null
  onChange: (tagName: string | null) => void
  inputLabel?: string
  inputVariant?: 'filled' | 'standard' | 'outlined' | undefined
  saveButtonText?: string
  disabled?: boolean
  errorText?: string
  helperText?: string
}) {
  const {value, onChange, disabled} = props
  const [name, setName] = useState('')
  const {value$, onReady} = useObserve(name)
  const [options, setOptions] = React.useState<string[]>([])
  const [loading, setLoading] = React.useState(false)
  const {flag: showCreateForm, toggle: toggleShowCreateForm} = useToggle()

  const {
    event: {slug},
  } = useEvent()
  const token = useAuthToken()

  const handleOnCreate = (tagName: string | null) => {
    toggleShowCreateForm()
    onChange(tagName)
  }

  useEffect(() => {
    if (!onReady) {
      return
    }

    value$
      .pipe(
        filter((value) => {
          return value.length >= MIN_SEARCH_CHARS
        }),
        tap(() => {
          setLoading(true)
        }),
        debounceTime(500),
        switchMap((name) => {
          const url = api(
            `/events/${slug}/integrations/high_level/tags?search=${name}`,
          )

          return ajax.get(url, {
            accessToken: token,
          })
        }),
        map((res) => res.response),
        tap(() => {
          setLoading(false)
        }),
      )
      .subscribe({
        next: (options) => {
          setOptions(options)
        },
      })

    onReady()
  }, [value$, token, slug, onReady])

  const noOptionsText = () => {
    if (name.length < MIN_SEARCH_CHARS) {
      return `Type to search. Minimum length: ${MIN_SEARCH_CHARS} characters.`
    }

    return 'Tag not found.'
  }

  return (
    <Box>
      <Autocomplete
        disabled={disabled}
        options={options}
        value={value}
        onChange={handleAutocomplete(onChange)}
        loading={loading}
        closeIcon=""
        aria-label={'tag name holder'}
        noOptionsText={noOptionsText()}
        renderInput={(params) => {
          return (
            <>
              <TextField
                {...params}
                onChange={onChangeStringHandler(setName)}
                variant={props.inputVariant ? props.inputVariant : 'standard'}
                fullWidth
                helperText={
                  props.errorText || props.helperText
                    ? props.errorText || props.helperText
                    : ''
                }
                error={Boolean(props.errorText)}
                label={props.inputLabel ? props.inputLabel : 'High level Tag'}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'tag name',
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Button
                      type="button"
                      color="primary"
                      size="large"
                      aria-label="create tag"
                      onClick={toggleShowCreateForm}
                      disabled={disabled}
                    >
                      Create new tag
                    </Button>
                  ),
                }}
              />
            </>
          )
        }}
      />
      <CreateNewTagdDialog
        visible={showCreateForm}
        onClose={toggleShowCreateForm}
        onCreate={handleOnCreate}
      />
    </Box>
  )
}

const CreateNewTagdDialog = (props: {
  visible: boolean
  onClose: () => void
  onCreate: (newTag: string | null) => void
}) => {
  const {visible, onCreate, onClose} = props

  return (
    <Dialog open={visible} onClose={onClose}>
      <DialogTitle>Create new tag</DialogTitle>
      <DialogContent>
        <CreateNewTagForm onCreate={onCreate} />
      </DialogContent>
    </Dialog>
  )
}

const Box = styled.div`
  position: relative;
`
